import React from 'react';
import { App, Button, Descriptions, Space, Tag, Tooltip, Typography, theme } from 'antd';
import { CopyOutlined, ExportOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Dash } from './statusVisuals';
import type { HealthDetail } from './healthDetailTypes';

const { Text } = Typography;

const TAG: React.CSSProperties = { margin: 0, fontSize: 12 };

// Identity block for HealthDetailPage. Three columns, so it costs two or three
// short rows instead of five full-width ones: the tables below are why the
// page was opened, and at 800px tall every row here pushes them down.
const HealthIdentity: React.FC<{ detail: HealthDetail }> = ({ detail }) => {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const isMobileGW = detail.type === 'mobile-gateway';
  const modeLabel = detail.checkMode === 'active-probing'
    ? 'Active Probing'
    : detail.checkMode === 'agent-heartbeat' ? 'Agent Heartbeat' : detail.checkMode;
  const tagList = (items: string[]) => (items.length === 0 ? <Dash /> : (
    <Space size={4} wrap>{items.map((t) => <Tag key={t} style={TAG}>{t}</Tag>)}</Space>
  ));

  return (
    <Descriptions
      bordered
      column={3}
      size="small"
      style={{ marginBottom: 12 }}
      styles={{ label: { width: 1, whiteSpace: 'nowrap' } }}
    >
      <Descriptions.Item label={isMobileGW ? 'Gateway IP' : 'Main IP'}>
        <Space size={6}>
          {detail.mainIP}
          {isMobileGW && (
            <Tooltip title="This is the relay front. The real egress per modem is shown below in the Modems section.">
              <InfoCircleOutlined aria-label="About the gateway IP" style={{ color: token.colorTextTertiary }} />
            </Tooltip>
          )}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label={isMobileGW ? 'Gateway country' : 'Country'}>
        {detail.country || (
          <Tooltip title="Country not yet detected (pending enrichment)"><span><Dash /></span></Tooltip>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Mode / Type">
        {/* Mode and type are facts, not states: neutral tags, no status colour. */}
        <Space size={4}>
          <Tag style={TAG}>{modeLabel}</Tag>
          <Tag style={TAG}>{isMobileGW ? 'Mobile gateway' : 'Dedicated'}</Tag>
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label="Networks">{tagList(detail.networks)}</Descriptions.Item>
      <Descriptions.Item label="Tags">{tagList(detail.tags)}</Descriptions.Item>
      <Descriptions.Item label="Key">
        <Text type="secondary" style={{ fontSize: 12 }}>
          {detail.key}
        </Text>
      </Descriptions.Item>
      {detail.sourceKey && (
        <Descriptions.Item label="Source" span={3}>
          <Space size={8} align="center" wrap>
            <Tag style={TAG}>{detail.sourceKey}</Tag>
            {detail.sourceId && (
              <Space size={0}>
                <Text type="secondary" style={{ fontSize: 12 }}>ID: {detail.sourceId}</Text>
                <Tooltip title="Copy source ID">
                  <Button
                    size="small"
                    type="text"
                    aria-label="Copy source ID"
                    icon={<CopyOutlined style={{ fontSize: 12 }} />}
                    onClick={() => {
                      navigator.clipboard.writeText(detail.sourceId!);
                      message.success('Source ID copied');
                    }}
                  />
                </Tooltip>
              </Space>
            )}
            {detail.sourceUrl && (
              <Tooltip title="Open in integrator">
                <Button size="small" type="link" icon={<ExportOutlined />} href={detail.sourceUrl} target="_blank">
                  Open
                </Button>
              </Tooltip>
            )}
          </Space>
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};

export default HealthIdentity;
